import { API_BASE as BASE_URL, getAuthHeaders, apiFetch } from './api-fetch';
import { API, buildPath } from './api-endpoints';
import { Seller } from '../types/seller';

// Get all sellers
export async function getSellers(): Promise<Seller[]> {
  try {
    const response = await apiFetch(`${BASE_URL}${API.sellers.list.path}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Failed to fetch sellers');
    }

    const data = await response.json();
    return data.data?.sellers ?? [];
  } catch (error) {
    console.error('Error fetching sellers:', error);
    throw error;
  }
}

// Get single seller — returns null if not found (404)
export async function getSeller(id: string): Promise<Seller | null> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.sellers.getById.path, { id })}`, {
      headers: getAuthHeaders(),
    });

    if (response.status === 404) {
      return null; // Seller doesn't exist yet — expected for first-time Cognito users
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch seller');
    }

    const data = await response.json();
    // Backend returns { success, data: { seller: { sellerId, ... } } }
    const raw = data.data?.seller ?? data.data;
    if (!raw) return null;
    return { ...raw, id: raw.id ?? raw.sellerId };
  } catch (error) {
    console.error('Error fetching seller:', error);
    throw error;
  }
}

// Create a new seller profile
export async function createSeller(sellerData: Partial<Seller> & { id: string }): Promise<Seller> {
  try {
    const response = await apiFetch(`${BASE_URL}${API.sellers.create.path}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(sellerData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create seller');
    }

    const data = await response.json();
    // Backend returns { success, data: { seller: { sellerId, ... } } }
    const raw = data.data?.seller ?? data.data;
    return { ...raw, id: raw.id ?? raw.sellerId };
  } catch (error) {
    console.error('Error creating seller:', error);
    throw error;
  }
}

// Update seller
export async function updateSeller(id: string, sellerData: Partial<Seller>): Promise<Seller> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.sellers.update.path, { id })}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(sellerData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update seller');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating seller:', error);
    throw error;
  }
}

// Seed sellers
export async function seedSellers(sellers: Seller[]): Promise<void> {
  try {
    const response = await apiFetch(`${BASE_URL}/sellers/seed`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ sellers }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to seed sellers');
    }

    const data = await response.json();
    console.log(`Seeded ${data.count} sellers`);
  } catch (error) {
    console.error('Error seeding sellers:', error);
    throw error;
  }
}

export const sellerAPI = {
  getSellers,
  getSeller,
  createSeller,
  updateSeller,
  seedSellers,
};
